import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {MDBModalRef, ModalDirective} from 'ng-uikit-pro-standard';
import {finalize, take, tap} from 'rxjs/operators';
import {IdAndLabel} from '../../../../common/model/id-and-label';
import {BuildingsService} from '../../../../common/services/buildings.service';
import {Localization} from '../../../../common/enums/localization';
import {ChecklistsService} from '../../../../common/services/checklists.service';
import {ActivatedRoute} from '@angular/router';
import {StructureType} from '../../../../common/enums/enums';
import {BuildingSummary} from "../../../../common/model/building-summary";
import {sortBy} from "../../../../../helpers/sorting";
import {pluck, where} from "../../../../../helpers/collections";
import {SnackBarService} from "../../../../../services/snackbar.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-copy-checklist-modal',
  templateUrl: './copy-checklist-modal.component.html',
  styleUrls: ['./copy-checklist-modal.component.scss']
})
export class CopyChecklistModalComponent implements OnInit {

  @Output()
  public readonly created$: EventEmitter<number> = new EventEmitter<number>();
  public readonly onCreated = this.created$.pipe(take(1)).toPromise();

  public readonly Localization = Localization;
  public formGroup: UntypedFormGroup;
  public isCopyingChecklist: boolean = false;
  public areBuildingsLoading: boolean = false;
  public buildings: BuildingSummary[] = [];
  public filteredList: IdAndLabel[] = [];
  public checklistId: number;
  public currentBuildingId: number;
  public sitesAndBuildings: BuildingSummary[];

  constructor(
      private readonly buildingService: BuildingsService,
      private readonly checklistsService: ChecklistsService,
      private readonly self: MDBModalRef,
      private readonly snackbar: SnackBarService,
      private readonly translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadBuildings();
  }


  public onSubmit() {
    if (this.formGroup.invalid) {
      Object.values(this.formGroup.controls)
        .forEach(c => c.markAsTouched());
    } else {
      this.duplicateChecklist();
    }
  }

  private duplicateChecklist() {
    this.isCopyingChecklist = true;
    const buildingList = this.convertFormGroupValueToArrayOfIds();
    this.checklistsService.duplicateChecklist(this.checklistId, buildingList)
      .pipe(
        tap(() => this.isCopyingChecklist = false)
      )
      .subscribe(
        () => {
          this.created$.emit(buildingList.length);
          this.snackbar.show(this.translate.instant(Localization.ChecklistHasBeenCopied));
          this.close();
        }
      );
  }

  public close(){
    this.self.hide();
  }

  private initForm(){
    this.formGroup = new UntypedFormGroup({});
  }

  private loadBuildings(){
    this.areBuildingsLoading = true;
    this.buildingService.getAllBuildingsIncludingChildren()
    .pipe(
      finalize(() => this.areBuildingsLoading = false)
    )
    .subscribe((buildings) => {
      this.buildings = buildings.filter(({id}) => id !== this.currentBuildingId);
      this.buildings.forEach(value => this.formGroup.registerControl(`${value.id}`, new UntypedFormControl(false)));
      this.sitesAndBuildings = this.buildings.filter(estate => estate.type === StructureType.SITE || !estate.parentStructureId)
        .sort(sortBy<BuildingSummary>('label'));
    });
  }

  public selectAll(event: any){
    event.currentTarget.checked ? this.patchAllFormControls(true) : this.patchAllFormControls(false);
  }

  private patchAllFormControls(isChecked: boolean){
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].patchValue(isChecked);
    });
  }

  public search(value: string) {
    if(value){
      this.filteredList = this.buildings.filter(b => b.label.toLowerCase().includes(value.toLowerCase()));
    }else{
      this.filteredList = this.buildings;
    }
  }

  private convertFormGroupValueToArrayOfIds(): number[] {
    return Object.keys(this.formGroup.value)
      .filter(k => this.formGroup.value[k] === true)
      .map(v => parseInt(v, 10))
      .filter(id => !this.isSiteId(id));
  }

  public isSite(realEstate: BuildingSummary) {
    return realEstate.type === StructureType.SITE;
  }

  private toggleAllBuildingsIn(site: BuildingSummary) {
    const value = this.formGroup.get(`${site.id}`).value;
    this.checkFormControlsFor(
      this.siteBuildings(site),
      value,
    );
  }

  private checkFormControlsFor(realEstate: BuildingSummary[], value: boolean): void {
    realEstate.map(({id}) => this.formGroup.get(`${id}`))
      .forEach(formControl => formControl.setValue(value))
  }

  public siteBuildings(realEstate: BuildingSummary) {
    return this.buildings.filter(({parentStructureId}) => parentStructureId === realEstate.id);
  }

  public get rowsForBuildings(): number {
    return Math.floor((this.buildings.length / 2)) + (this.buildings.length % 2);
  }

  public updateSelection(realEstate: BuildingSummary) {
    if (realEstate.type === StructureType.SITE) {
      this.toggleAllBuildingsIn(realEstate);
    }
  }

  private isSiteId(id: number): boolean {
    return this.buildings.filter(where('type', StructureType.SITE))
      .map(pluck('id'))
      .some(siteId => siteId === id);
  }
}
